/**
 * This component is a resolver. It is meant to be used by page templates using
 * data from a CMS.
 *
 * You can send the data you receive from the CMS to the props of this components
 * by spreading them (like <Hero {...cmsData} />)
 * and then make these props fit the final module.
 */
import React from "react"
import { Hero as HeroModule } from "@bw/modules"
import { Button, CmsImage } from "@bw/bits"
import { graphql } from "gatsby"

const Hero = ({
  title,
  suptitle,
  subtitle,
  buttons,
  image,
  longVideoFile,
  shortVideoFile,
  fullPage,
}) => {
  return (
    <HeroModule
      {...{ suptitle, title, subtitle, fullPage }}
      video={shortVideoFile?.url}
      videoPopup={longVideoFile?.url}
      image={<CmsImage src={image} alt={image.alt} />}
      focus={image.has_focus ? image.focus_css : undefined}
      negative={true}
      buttons={buttons.map((button, i) => {
        if (button.linkType.value === "url" && button.url) {
          return (
            <Button
              key={i}
              label={button.linkText}
              href={button.url}
              external={button.isExternal}
              modal={button.openInModal}
              primary
              inversed={i !== 0}
            />
          )
        }

        if (button.linkType.value === "entry" && button.entry) {
          return (
            <Button
              key={i}
              label={button.linkText}
              to={button.entry.url}
              primary
              inversed={i !== 0}
            />
          )
        }

        return null
      })}
    />
  )
}

/**
 * This generates a GraphQL Fragment that can be used in a page query:
 *
 * Example:
 * ... on HeroModule {
 *   ...HeroModuleFragment
 * }
 *
 * This is also what you will get in the props of your LogoGrid
 * resolver component.
 */
export const query = graphql`
  fragment HeroModuleFragment on HeroModule {
    buttons {
      isExternal
      openInModal
      linkType {
        value
      }
      url
      linkText
      entry {
        url
      }
    }
    image {
      alt
      has_focus
      focus_css
      localImage {
        childImageSharp {
          gatsbyImageData(width: 2500)
        }
      }
    }
    longVideoFile {
      url
    }
    shortVideoFile {
      url
    }
    fullPage
  }
`

export { Hero }
